import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEdit,
  FaTh,
  FaList,
  FaTrash,
  FaPrint,
  FaFilter,
} from "react-icons/fa";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { useStatuses } from "../../hooks/useStatuses";
import { showCurrencySymbol, showTranslatedValue, formatTime12Hour, getOrderStatusColor } from "../../helper/generalHelper";
import ReceiptModal from "../../components/restaurant/Order/PrintBill";
import TableSearch from "../../components/common/TableSearch";
import Pagination from "../../components/common/Pagination";
import { usePagination } from "../../hooks/usePagination";

const orderTypeTabs = [
  { key: "all", label: "All" },
  { key: "QSR", label: "QSR" },
  { key: "table", label: "Table" },
  { key: "takeaway", label: "Takeaway" },
  { key: "online", label: "Online" },
  { key: "third_party", label: "Third Party" },
];

const ReceiptPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();

  const isStaff = window.location.pathname.includes("/restaurant-staff/");
  const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
  const navigatePrefix = isStaff ? "restaurant-staff/employee" : "restaurant";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("preferred_order_view") || "list");
  const { statusesLoading, findStatus } = useStatuses();
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    localStorage.setItem("preferred_order_view", view);
  }, [view]);
  
  // Status filter states
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("all");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("all");
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowStatusFilter(false);
      }
    };

    if (showStatusFilter) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStatusFilter]); 

  const handlePrintClick = (order) => {
    setSelectedOrder(order);
    setIsPrintOpen(true);
  };

  useEffect(() => {
    if (statusesLoading === false) {
      fetchOrders();
    }
  }, [statusesLoading]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${apiPrefix}/order/all`);
      setOrders(response.data || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (order) => {
    navigate(`/${navigatePrefix}/orders/edit/${order.id}`);
  };

  const handleDeleteClick = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    try {
      const response = await api.delete(`${apiPrefix}/order/delete?id=${orderId}`);
      setOrders((prev) => prev.filter((order) => order.id !== orderId));
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order.");
    }
  };

  // Get unique order statuses from orders
  const getUniqueOrderStatuses = () => {
    return [1, 3, 4, 10];
  };

  // Get unique payment statuses from orders
  const getUniquePaymentStatuses = () => {
    return [17, 18, "Cash", "Card", "UPI"];
  };

  // Filter orders based on selected filters
  const filteredOrders = orders.filter((order) => {
    // Order type filter
    const typeMatch = activeTab === "all" || order.order_type === activeTab;
    
    // Order status filter
    const orderStatusMatch = selectedOrderStatus === "all" || 
      order.order_status == selectedOrderStatus;
    
    // Payment status filter
    let paymentStatusMatch = selectedPaymentStatus === "all";
    if (!paymentStatusMatch) {
      if (["Cash", "Card", "UPI"].includes(selectedPaymentStatus)) {
        paymentStatusMatch = order.payment_mode?.toLowerCase() === selectedPaymentStatus.toLowerCase() && order.payment_status == 17;
      } else {
        paymentStatusMatch = order.payment_status == selectedPaymentStatus;
      }
    }
    
    return typeMatch && orderStatusMatch && paymentStatusMatch;
  });

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    searchTerm,
    setSearchTerm,
    paginatedData,
    totalItems,
    totalPages,
  } = usePagination(filteredOrders, ["customer_name", "id", "table_name", "table_number"]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedOrderStatus("all");
    setSelectedPaymentStatus("all");
  };

  // Check if any filter is active
  const isFilterActive = selectedOrderStatus !== "all" || selectedPaymentStatus !== "all";

  if (loading || statusesLoading) {
    return (
      <p className="text-center text-rose-600 text-lg mt-10">
        Loading orders...
      </p>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gray-50">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <FaArrowLeft
            className="text-2xl cursor-pointer text-gray-700 hover:text-gray-900"
            onClick={() => navigate(-1)}
          />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 capitalize">
            Receipt
          </h1>
        </div>
      </div>

      {/* Active Filters Display */}
      {isFilterActive && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium text-blue-800">Active Filters:</span>
            
            {selectedOrderStatus !== "all" && (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                Order: {showTranslatedValue(findStatus(selectedOrderStatus, "orders"), "label")}
                <button
                  onClick={() => setSelectedOrderStatus("all")}
                  className="text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            
            {selectedPaymentStatus !== "all" && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                Payment: {["Cash", "Card", "UPI"].includes(selectedPaymentStatus) ? selectedPaymentStatus : showTranslatedValue(findStatus(selectedPaymentStatus, "payment"), "label")}
                <button
                  onClick={() => setSelectedPaymentStatus("all")}
                  className="text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
            
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 ml-auto"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Tabs & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {orderTypeTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition ${
                activeTab === tab.key
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-blue-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3 w-full lg:w-auto">
          {/* Filter Button */}
          <div className="relative order-1 md:order-none z-10" ref={filterRef}>
            <button
              className={`p-2 rounded-md flex items-center gap-2 ${
                isFilterActive ? "bg-blue-100 text-blue-600" : "bg-gray-100"
              }`}
              onClick={() => setShowStatusFilter(!showStatusFilter)}
            >
              <FaFilter />
              <span className="hidden sm:inline">Filter</span>
              {isFilterActive && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  !
                </span>
              )}
            </button>

            {/* Status Filter Dropdown */}
            {showStatusFilter && (
              <div className="absolute left-0 sm:right-0 sm:left-auto top-12 bg-white rounded-lg shadow-lg border p-4 z-50 w-[280px] max-w-[90vw]">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-800">Filter Receipts</h3>
                  {isFilterActive && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* Order Status Filter */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Status
                  </label>
                  <select
                    value={selectedOrderStatus}
                    onChange={(e) => setSelectedOrderStatus(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Order Statuses</option>
                    {getUniqueOrderStatuses().map((status) => (
                      <option key={status} value={status}>
                        {showTranslatedValue(findStatus(status, "orders"), "label") || `Status ${status}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Payment Status Filter */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    value={selectedPaymentStatus}
                    onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Payment Statuses</option>
                    {getUniquePaymentStatuses().map((status) => (
                      <option key={status} value={status}>
                        {["Cash", "Card", "UPI"].includes(status) 
                          ? `Mode - ${status}` 
                          : (showTranslatedValue(findStatus(status, "payment"), "label") || `Payment ${status}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>
                    Showing {filteredOrders.length} of {orders.length} receipts
                  </span>
                  <button
                    onClick={() => setShowStatusFilter(false)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="order-3 md:order-none w-full md:w-auto md:flex-1 min-w-[200px]">
            <TableSearch 
              value={searchTerm} 
              onChange={setSearchTerm} 
              placeholder="Search receipts..." 
              className="w-full"
            />
          </div>

          {/* View Toggle Buttons */}
          <div className="flex items-center gap-2 order-2 md:order-none">
            <button
              className={`p-2 rounded-md ${
                view === "list" ? "bg-gray-300" : "bg-gray-100"
              }`}
              onClick={() => setView("list")}
            >
              <FaList />
            </button>
            <button
              className={`p-2 rounded-md ${
                view === "grid" ? "bg-gray-300" : "bg-gray-100"
              }`}
              onClick={() => setView("grid")}
            >
              <FaTh />
            </button>
          </div>
        </div>
      </div>

      {/* Orders Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredOrders.length} receipt{filteredOrders.length !== 1 ? 's' : ''}
        {isFilterActive && ` (filtered from ${orders.length} total)`}
      </div>

      {/* Orders */}
      <div className={`w-full ${view === "list" ? "overflow-x-auto pb-4" : ""}`}>
        {view === "list" ? (
          <table className="min-w-full text-left text-sm whitespace-nowrap bg-white rounded-lg overflow-hidden border border-gray-200">
            <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">ORDER INFO</th>
                <th className="px-6 py-4">DATE & TIME</th>
                <th className="px-6 py-4">TYPE</th>
                <th className="px-6 py-4">STATUS</th>
                <th className="px-6 py-4">PAYMENT</th>
                <th className="px-6 py-4">TOTAL</th>
                <th className="px-6 py-4 text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No receipts found.
                  </td>
                </tr>
              )}
              {paginatedData.map((order) => {
                const dateObj = new Date(order.order_date || new Date());
                const shortDate = `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`;
                const shortId = order.daily_order_number 
                  ? `${shortDate}-${String(order.daily_order_number).padStart(3, '0')}`
                  : `${shortDate}-${(order.id || "").substring(0, 4).toUpperCase()}`;
                
                return (
                  <tr 
                    key={order.id} 
                    className="hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => navigate(`/${navigatePrefix}/singleorders/${order.id}`, { state: { orderData: order } })}
                  >
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900">{order.customer_name?.trim() || "Guest Customer"}</div>
                      <div className="text-xs text-gray-500 font-mono mt-1">#{shortId}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div>{order.order_date}</div>
                      <div className="text-xs text-gray-400">{formatTime12Hour(order.order_time)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full capitalize font-medium">
                        {order.order_type === "table" ? `Table - ${order.table_name || order.table_number || "Table"}` : order.order_type.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-3 py-1 rounded-full capitalize font-medium ${getOrderStatusColor(order.order_status)}`}>
                        {order.order_status ? showTranslatedValue(findStatus(order.order_status, "orders"), "label") : "Unknown"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-3 py-1 rounded-full capitalize font-medium ${order.payment_status == "17" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {order.payment_status ? showTranslatedValue(findStatus(order.payment_status, "payment"), "label") : "Unknown"}
                        {(order.payment_status == 17 || order.payment_status == '17') && order.payment_mode && ` - ${order.payment_mode}`}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-800">
                      {showCurrencySymbol(parseFloat(order.grand_total || 0).toFixed(2))}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePrintClick(order); }}
                          className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition"
                          title="Print Receipt"
                        >
                          <FaPrint />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditClick(order); }}
                          className="text-blue-600 hover:text-blue-800 px-3 py-1 bg-blue-50 hover:bg-blue-100 rounded-md text-xs font-semibold transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(order.id); }}
                          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition"
                          title="Delete Order"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
        <div
          className="order-management-grid grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
        {filteredOrders.length === 0 && (
          <div className="text-center text-gray-500 col-span-full mt-6 p-8 bg-white rounded-lg border border-gray-200">
            <p className="text-lg mb-2">No receipts found</p>
            <p className="text-sm text-gray-400 mb-4">
              {isFilterActive 
                ? "Try adjusting your filters to see more results" 
                : "No receipts have been generated yet"
              }
            </p>
            {isFilterActive && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}

        {paginatedData.map((order) => {
          const dateObj = new Date(order.order_date || new Date());
          const shortDate = `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`;
          const shortId = order.daily_order_number 
            ? `${shortDate}-${String(order.daily_order_number).padStart(3, '0')}`
            : `${shortDate}-${(order.id || "").substring(0, 4).toUpperCase()}`;

          return (
          <div
            key={order.id}
            className="bg-white rounded-2xl shadow-md border border-gray-200 p-5 hover:shadow-lg transition relative"
          >
            <button
              className="absolute right-3 top-3 p-2 rounded-md bg-gray-100 hover:bg-gray-200"
              onClick={() => handlePrintClick(order)}
            >
              <FaPrint className="text-lg text-gray-700" />
            </button>

            <div>
              <div className="mb-2">
                <div className="flex justify-between items-start">
                  <span className="font-bold text-gray-900 text-lg">
                    {order.customer_name?.trim() || "Guest Customer"}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-gray-500 font-mono">
                    #{shortId}
                  </span>
                  <span className="text-[11px] text-gray-500">
                    {order.order_date} {formatTime12Hour(order.order_time)}
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center mb-2">
                <span className="text-xs bg-blue-600 text-white px-3 py-1 rounded-full capitalize">
                  {order.order_type === "table"
                    ? `Table - ${order.table_name || order.table_number || "Table"}`
                    : order.order_type.replace("_", " ")}
                </span>
                <span className="text-xs bg-green-600 text-white px-3 py-1 rounded-full font-medium">
                  {showCurrencySymbol(parseFloat(order.grand_total || 0).toFixed(2))}
                </span>
              </div>

              <div className="flex justify-end gap-2 items-center mt-2 flex-wrap">
                <span className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${getOrderStatusColor(order.order_status)}`}>
                  {order.order_status
                    ? showTranslatedValue(
                        findStatus(order.order_status, "orders"),
                        "label"
                      )
                    : "Unknown"}
                </span>
                <span
                  className={`text-xs px-3 py-1 rounded-full capitalize font-medium ${
                    order.payment_status == "17"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {order.payment_status
                    ? showTranslatedValue(
                        findStatus(order.payment_status, "payment"),
                        "label"
                      )
                    : "Unknown"}
                  {(order.payment_status == 17 || order.payment_status == '17') && order.payment_mode && ` - ${order.payment_mode}`}
                </span>
                {parseFloat(order.extra_discount_amount || 0) > 0 && (
                  <span className="text-[10px] px-2 py-1 rounded-full uppercase font-bold border bg-red-100 text-red-700 border-red-200">
                    Disc. {order.extra_discount_type ? ((order.extra_discount_type === '%' || order.extra_discount_type === 'percentage') ? `${parseFloat(order.extra_discount || 0)}%` : showCurrencySymbol(parseFloat(order.extra_discount_amount || 0))) : 'Applied'}
                  </span>
                )}
              </div>

              {order.order_details?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {order.order_details.slice(0, 3).map((item) => {
                    const { menu } = item;
                    const imageUrl = menu?.menu_images?.[0] || null;

                    return (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 bg-gray-100 rounded-md px-3 py-2"
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={menu?.title || "Menu Item"}
                            className="w-12 h-12 rounded object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-gray-300 flex items-center justify-center text-gray-600 text-sm">
                            N/A
                          </div>
                        )}

                        <div className="flex-1">
                          <p className="font-medium text-gray-800">
                            {showTranslatedValue(menu, "title") ||
                              `Menu ID: ${item.menu_id}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            Qty: {item.quantity} × {showCurrencySymbol(parseFloat(item.price).toFixed(2))}
                          </p>
                          {parseFloat(item.tax || item.menu?.tax || 0) > 0 && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Tax ({parseFloat(item.tax || item.menu?.tax || 0).toFixed(2)}%): {showCurrencySymbol(((item.price * item.quantity * parseFloat(item.tax || item.menu?.tax || 0)) / 100).toFixed(2))}
                            </p>
                          )}
                          {parseFloat(item.extra_tax || 0) > 0 && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Extra Tax ({parseFloat(item.extra_tax).toFixed(2)}%): {showCurrencySymbol(((item.price * item.quantity * parseFloat(item.extra_tax)) / 100).toFixed(2))}
                            </p>
                          )}
                          {parseFloat(item.discount || 0) > 0 && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Discount ({(item.discount_type?.toLowerCase() === 'percentage' || item.discount_type === '%') ? `${parseFloat(item.discount).toFixed(2)}%` : 'Fixed'}): -{showCurrencySymbol((item.discount_type?.toLowerCase() === 'percentage' || item.discount_type === '%') ? ((item.price * item.quantity * parseFloat(item.discount)) / 100).toFixed(2) : parseFloat(item.discount).toFixed(2))}
                            </p>
                          )}
                          {parseFloat(item.other_cost || 0) > 0 && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Other Cost: {showCurrencySymbol(parseFloat(item.other_cost).toFixed(2))}
                            </p>
                          )}
                        </div>

                        <p className="text-sm font-semibold text-gray-700">
                          {showCurrencySymbol(parseFloat(item.total_price).toFixed(2))}
                        </p>
                      </div>
                    );
                  })}

                  {order.order_details?.length > 3 && (
                    <div className="text-center pt-2 border-t">
                      <span className="text-sm text-gray-500">
                        +{order.order_details.length - 3} more items
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <FaTrash
                  className="text-red-500 cursor-pointer hover:text-red-700 transition"
                  onClick={() => handleDeleteClick(order.id)}
                  title="Delete Order"
                />
                <div className="flex items-center gap-4">
                  <button
                    className="text-blue-600 hover:text-blue-800 px-3 py-1 bg-blue-50 hover:bg-blue-100 rounded-md text-xs font-semibold transition"
                    onClick={() => handleEditClick(order)}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() =>
                      navigate(`/${navigatePrefix}/singleorders/${order.id}`, { state: { orderData: order } })
                    }
                    className="text-sm text-blue-600 hover:underline font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        );})}
        </div>
        )}
      </div>

      {/* Pagination */}
      {filteredOrders.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {isPrintOpen && selectedOrder && (
        <ReceiptModal
          open={isPrintOpen}
          onClose={() => setIsPrintOpen(false)}
          orderData={selectedOrder}
        />
      )}
    </div>
  );
};

export default ReceiptPage;